import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import FoncierSheet from "@/components/FoncierSheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  X, Building2, Gauge, TrendingDown, Landmark, ShieldAlert, MapPin, ArrowUpRight,
  Loader2, PlusCircle, Sparkles, History, FileText, Gavel, FileDown,
  ExternalLink, Phone, Mail, CalendarDays, ShieldCheck, UserRound, Link2Off, Lock,
  Coins, GraduationCap, Train, ShoppingBag, Volume2,
} from "lucide-react";

const CONTACT_TYPE_KEYS = {
  agence: "ctAgence", particulier: "ctParticulier", notaire: "ctNotaire",
  "enchères": "ctEncheres", encheres: "ctEncheres", autre: "ctAutre",
};

const scoreColor = (s) =>
  s >= 70 ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : s >= 40 ? "bg-amber-100 text-amber-800 border-amber-200"
      : "bg-slate-100 text-slate-600 border-slate-200";

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <h3 className="mb-3 flex items-center gap-2 font-display text-lg text-[#0F172A]">
      <Icon className="h-4 w-4 text-[#C6A87C]" /> {title}
    </h3>
    {children}
  </div>
);

const List = ({ items, empty }) =>
  items && items.length ? (
    <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
  ) : <p className="text-sm text-slate-400">{empty}</p>;

const Metric = ({ label, value, mention, accent }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <p className={`mt-1 text-lg font-bold ${accent || "text-[#0F172A]"}`}>{value}</p>
    {mention && <p className="mt-0.5 text-[10px] italic text-slate-400">{mention}</p>}
  </div>
);

const EnvItem = ({ icon: Icon, label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3 text-center">
    <Icon className="mx-auto h-4 w-4 text-[#C6A87C]" />
    <p className="mt-1 text-xs text-slate-500">{label}</p>
    <p className="text-xs font-medium italic text-slate-400">{value}</p>
  </div>
);

const fmtDist = (m) => (m == null ? "" : m < 1000 ? `${m} m` : `${(m / 1000).toFixed(1).replace(".", ",")} km`);

const EnvBlock = ({ icon: Icon, title, data, na, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid={testid}>
    <div className="mb-1.5 flex items-center justify-between">
      <span className="flex items-center gap-1.5 text-sm font-medium text-[#0F172A]"><Icon className="h-4 w-4 text-[#C6A87C]" /> {title}</span>
      {data?.available && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{data.count}</span>}
    </div>
    {data?.available ? (
      data.count === 0 ? <p className="text-xs italic text-slate-400">{na}</p> : (
        <ul className="space-y-0.5 text-xs text-slate-600">
          {(data.nearest || []).map((it, i) => (
            <li key={i} className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate">{it.name}{it.kind && it.kind !== "gare" ? <span className="text-slate-400"> · {it.kind}</span> : ""}</span>
              {it.dist_m != null && <span className="shrink-0 text-slate-400">{fmtDist(it.dist_m)}</span>}
            </li>
          ))}
        </ul>
      )
    ) : <p className="text-xs italic text-slate-400">{na}</p>}
  </div>
);

export default function RadarDetail({ listing, onClose, onChanged, onDeep, pluEntitled = true }) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);
  const [deepLoading, setDeepLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [env, setEnv] = useState(null);
  const [envLoading, setEnvLoading] = useState(false);
  const [showLoc, setShowLoc] = useState(false);
  const [showFoncier, setShowFoncier] = useState(false);
  const isFR = (listing?.country || "FR").toUpperCase() === "FR";
  const nc = t("radar.detail.dataUnavailable");
  const contactTypeLabel = (ct) => (ct ? t(`radar.detail.${CONTACT_TYPE_KEYS[ct] || "ctAutre"}`) : t("radar.detail.ctUnknown"));
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  useEffect(() => {
    if (!listing?.id) return;
    const g = listing.analysis?.geo || {};
    const hasGeo = (g.lat != null && g.lon != null) || (listing.geo_lat != null && listing.geo_lon != null);
    if (!hasGeo) { setEnv({ available: false }); return; }
    setEnvLoading(true);
    api.get(`/radar/listings/${listing.id}/environment`)
      .then((r) => setEnv(r.data))
      .catch(() => setEnv({ available: false }))
      .finally(() => setEnvLoading(false));
  }, [listing?.id]);
  if (!listing) return null;
  const a = listing.analysis || {};
  const geo = a.geo || {};
  const dvf = a.dvf || {};
  const plu = a.plu || {};
  const risks = a.georisques || {};
  const parcelle = a.parcelle || {};
  const ai = a.ai || {};
  const scoring = a.scoring || {};
  const pm2 = a.price_m2;

  const sourceVerified = !!(listing.url && String(listing.url).startsWith("http") && listing.source_host);
  // Défensif : le téléphone peut être un objet (anciennes données) — on n'affiche jamais un objet brut.
  const phoneStr = (() => {
    const p = listing.contact_phone;
    if (!p) return null;
    if (typeof p === "string") return p;
    if (typeof p === "object") return p.formattedPhoneWithPrefix || p.phoneNumberForMobileDialing || p.formattedPhone || p.phoneNumber || null;
    return String(p);
  })();
  const contactAvailable = !!(listing.contact_name || phoneStr || listing.contact_email);
  const pubDate = listing.published_at ? new Date(listing.published_at) : null;
  const pubDateStr = pubDate && !isNaN(pubDate.getTime()) ? pubDate.toLocaleDateString("fr-FR") : (listing.published_at || null);

  // Comparaison de marché (DVF) — écart et marge estimée dérivés de données réelles uniquement
  const gapPct = (dvf.available && pm2 && dvf.median_m2) ? Math.round(((pm2 - dvf.median_m2) / dvf.median_m2) * 100) : null;
  const marginEst = (dvf.available && pm2 && dvf.median_m2 && listing.surface && pm2 < dvf.median_m2)
    ? Math.round((dvf.median_m2 - pm2) * listing.surface) : null;
  const fmtEur = (v) => `${Number(v).toLocaleString("fr-FR")} €`;

  const addToOpportunities = async () => {
    setAdding(true);
    try {
      await api.post(`/radar/listings/${listing.id}/to-opportunity`);
      toast.success(t("radar.detail.addedOk"));
      setAdded(true);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("radar.detail.addFail"));
    } finally { setAdding(false); }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get(`/radar/listings/${listing.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `RADAR-${(listing.city || "bien").replace(/\s+/g, "-")}-${listing.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("radar.detail.pdfOk"));
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) toast.error(t("radar.detail.pdfForbidden"));
      else toast.error(t("radar.detail.pdfFail"));
    } finally { setPdfLoading(false); }
  };

  const mapSrc = geo.lat && geo.lon
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${geo.lon - 0.006},${geo.lat - 0.004},${geo.lon + 0.006},${geo.lat + 0.004}&layer=mapnik&marker=${geo.lat},${geo.lon}`
    : null;

  return (
    <>
      <div className="fixed inset-0 z-[2000] bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="radar-detail-backdrop" />
      <div className="fixed inset-0 z-[2000] flex items-start justify-center overflow-y-auto p-4 sm:p-8" onClick={onClose}>
        <div className="w-full max-w-4xl rounded-2xl bg-[#F8F6F1] shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="radar-detail">
          {/* header */}
          <div className="flex items-start justify-between gap-4 rounded-t-2xl bg-[#0F172A] p-6 text-white">
            <div className="min-w-0">
              <p className="overline text-[#C6A87C]">{t("radar.detail.overline")}</p>
              <h2 className="mt-1 truncate font-display text-2xl">{listing.title}</h2>
              <p className="mt-1 text-sm text-slate-300">{[listing.city, listing.quartier, listing.property_type].filter(Boolean).join(" · ") || "—"}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className={`rounded-full border px-3 py-1 text-sm font-bold ${scoreColor(listing.score)}`}>{listing.score}/100</span>
              <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="radar-detail-close"><X className="h-5 w-5" /></button>
            </div>
          </div>

          <div className="space-y-4 p-6">
            {/* Provenance & contact — obligatoire sur chaque fiche */}
            <Section icon={ExternalLink} title={t("radar.detail.secSource")} testid="radar-sec-source">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                {sourceVerified ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200" data-testid="radar-source-verified">
                    <ShieldCheck className="mr-1 h-3.5 w-3.5" /> {t("radar.detail.sourceVerified")}
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-200" data-testid="radar-source-unverified">
                    <ShieldAlert className="mr-1 h-3.5 w-3.5" /> {t("radar.detail.sourceUnverified")}
                  </Badge>
                )}
                {!contactAvailable && (
                  <Badge className="bg-slate-100 text-slate-600 border border-slate-200" data-testid="radar-contact-unavailable">
                    {t("radar.detail.contactUnavailable")}
                  </Badge>
                )}
              </div>

              <div className="grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                <p>{t("radar.detail.site")} <b className="text-[#0F172A]" data-testid="radar-source-host">{listing.source_host || t("radar.detail.notVerified")}</b></p>
                <p className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-[#C6A87C]" /> {t("radar.detail.pubDate")} <b className="text-[#0F172A]">{pubDateStr || nc}</b></p>
                <p className="flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5 text-[#C6A87C]" /> {t("radar.detail.contactType")} <b className="text-[#0F172A]" data-testid="radar-contact-type">{contactTypeLabel(listing.contact_type)}</b></p>
                <p>{t("radar.detail.seller")} <b className="text-[#0F172A]">{listing.contact_name || nc}</b></p>
                <p className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-[#C6A87C]" /> {t("radar.detail.phone")} <b className="text-[#0F172A]">{phoneStr || nc}</b></p>
                <p className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-[#C6A87C]" /> {t("radar.detail.email")} <b className="text-[#0F172A]">{listing.contact_email || nc}</b></p>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {listing.url ? (
                  <a href={listing.url} target="_blank" rel="noreferrer" data-testid="radar-view-original">
                    <Button size="sm" className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
                      <ExternalLink className="mr-1.5 h-4 w-4" /> {t("radar.detail.viewOriginal")}
                    </Button>
                  </a>
                ) : (
                  <Button size="sm" disabled variant="outline" className="rounded-full" data-testid="radar-no-original">
                    <Link2Off className="mr-1.5 h-4 w-4" /> {t("radar.detail.noOriginal")}
                  </Button>
                )}
                {phoneStr && (
                  <a href={`tel:${phoneStr}`} data-testid="radar-contact-phone-btn">
                    <Button size="sm" variant="outline" className="rounded-full border-[#C6A87C] text-[#0F172A]">
                      <Phone className="mr-1.5 h-4 w-4" /> {t("radar.detail.contactSeller")}
                    </Button>
                  </a>
                )}
                {!phoneStr && listing.contact_email && (
                  <a href={`mailto:${listing.contact_email}`} data-testid="radar-contact-email-btn">
                    <Button size="sm" variant="outline" className="rounded-full border-[#C6A87C] text-[#0F172A]">
                      <Mail className="mr-1.5 h-4 w-4" /> {t("radar.detail.contactEmail")}
                    </Button>
                  </a>
                )}
              </div>

              {!contactAvailable && listing.url && (
                <p className="mt-3 rounded-lg bg-slate-50 p-2.5 text-xs text-slate-600 flex items-center gap-1.5" data-testid="radar-contact-via-original">
                  <ExternalLink className="h-3.5 w-3.5 text-[#C6A87C]" /> {t("radar.detail.contactViaOriginal")}
                </p>
              )}

              {!sourceVerified && (
                <p className="mt-3 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="radar-source-warning">
                  {t("radar.detail.sourceWarning")}
                </p>
              )}
            </Section>

            {listing.analysis?.warehouse?.applied && (
              <Section icon={Building2} title={t("radar.detail.secWarehouse")} testid="radar-sec-warehouse">
                {listing.analysis.warehouse.available ? (
                  <div className="grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                    <p>{t("radar.detail.whRadius")} <b className="text-[#0F172A]">{listing.analysis.warehouse.radius_used_km} km</b></p>
                    <p>{t("radar.detail.whCount")} <b className="text-[#0F172A]">{listing.analysis.warehouse.count}</b></p>
                    <p>{t("radar.detail.whAvg")} <b className="text-[#0F172A]">{listing.analysis.warehouse.avg_m2 ? `${Number(listing.analysis.warehouse.avg_m2).toLocaleString("fr-FR")} €/m²` : nc}</b></p>
                    <p>{t("radar.detail.whPm2")} <b className="text-[#0F172A]">{listing.analysis.warehouse.price_m2 ? `${Number(listing.analysis.warehouse.price_m2).toLocaleString("fr-FR")} €/m²` : nc}</b></p>
                    {listing.analysis.warehouse.gap_pct !== null && listing.analysis.warehouse.gap_pct !== undefined && (
                      <p className="sm:col-span-2">{t("radar.detail.whGap")}
                        <b className={`ml-1 ${listing.analysis.warehouse.gap_pct <= 0 ? "text-emerald-700" : "text-red-600"}`}>
                          {listing.analysis.warehouse.gap_pct > 0 ? "+" : ""}{listing.analysis.warehouse.gap_pct}%
                        </b>
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-amber-700" data-testid="radar-warehouse-insufficient">{listing.analysis.warehouse.reason || t("radar.detail.whInsufficient")}</p>
                )}
                {listing.analysis.warehouse.note && <p className="mt-2 text-xs italic text-slate-400">{listing.analysis.warehouse.note}</p>}
              </Section>
            )}

            {listing.kind === "auction" && (
              <Section icon={Gavel} title={t("radar.detail.secAuction")} testid="radar-sec-auction">
                <div className="grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                  <p>{t("radar.detail.askingPrice")} <b className="text-[#0F172A]">{listing.mise_a_prix ? `${Number(listing.mise_a_prix).toLocaleString("fr-FR")} €` : nc}</b></p>
                  <p>{t("radar.detail.saleDate")} <b className="text-[#0F172A]">{listing.sale_date ? new Date(listing.sale_date).toLocaleDateString("fr-FR") : nc}</b>
                    {listing.sale_date && (() => { const j = Math.ceil((new Date(listing.sale_date).getTime() - Date.now()) / 86400000); return j >= 0 ? <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-bold ${j <= 7 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{j > 0 ? `J-${j}` : t("radar.results.today")}</span> : null; })()}
                  </p>
                  <p className="sm:col-span-2">{t("radar.detail.jurisdiction")} <b className="text-[#0F172A]">{listing.jurisdiction || nc}</b></p>
                  {listing.reference && <p className="sm:col-span-2 text-xs text-slate-400">{t("radar.detail.dossierRef")} {listing.reference}</p>}
                  {listing.dossier && <p className="sm:col-span-2 rounded-lg bg-slate-50 p-3 text-slate-600">{listing.dossier}</p>}
                </div>
              </Section>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Badge className="bg-[#C6A87C] text-[#0F172A]">{listing.band}</Badge>
              <Badge variant="outline">{t("radar.detail.status", { status: listing.status })}</Badge>
              {listing.current_price ? <Badge variant="outline">{Number(listing.current_price).toLocaleString("fr-FR")} €</Badge> : null}
              {pm2 ? <Badge variant="outline">{pm2.toLocaleString("fr-FR")} €/m²</Badge> : null}
              <div className="ml-auto flex gap-2">
                {isFR && (
                  <Button onClick={() => setShowFoncier(true)} size="sm" data-testid="radar-foncier-btn" className="rounded-full bg-[#C6A87C] text-[#0F172A] hover:bg-[#b7965f]">
                    <Landmark className="mr-1.5 h-4 w-4" /> {t("radar.foncier.openBtn", "Analyse foncière et urbanistique")}
                  </Button>
                )}
                {listing.analysis_level === "quick" && onDeep && (
                  <Button onClick={async () => { setDeepLoading(true); await onDeep(listing.id); setDeepLoading(false); }} disabled={deepLoading} size="sm" data-testid="radar-deep-btn" variant="outline" className="rounded-full">
                    {deepLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />} {t("radar.detail.deepBtn")}
                  </Button>
                )}
                {listing.url && <a href={listing.url} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="rounded-full"><ArrowUpRight className="mr-1.5 h-4 w-4" /> {t("radar.detail.adBtn")}</Button></a>}
                <Button onClick={downloadPdf} disabled={pdfLoading} size="sm" data-testid="radar-pdf-btn" variant="outline" className="rounded-full">
                  {pdfLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileDown className="mr-1.5 h-4 w-4" />} {t("radar.detail.pdfBtn")}
                </Button>
                <Button onClick={addToOpportunities} disabled={adding || added || listing.opportunity_id} size="sm" data-testid="radar-add-opp" className="rounded-full bg-[#0F172A] text-white hover:bg-[#1e293b]">
                  {adding ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PlusCircle className="mr-1.5 h-4 w-4" />}
                  {(added || listing.opportunity_id) ? t("radar.detail.alreadyAdded") : t("radar.detail.addToOpp")}
                </Button>
              </div>
            </div>

            {/* Synthèse — prix, prix/m², comparaison marché, marge, rentabilité, score (100% réel) */}
            <Section icon={Coins} title={t("radar.detail.secSummary")} testid="radar-sec-summary">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Metric label={t("radar.detail.sumAsking")} value={listing.current_price ? fmtEur(listing.current_price) : nc} />
                <Metric label={t("radar.detail.sumPm2")} value={pm2 ? `${Number(pm2).toLocaleString("fr-FR")} €/m²` : nc} />
                <Metric label={t("radar.detail.sumScore")} value={`${listing.score}/100`}
                  accent={listing.score >= 70 ? "text-emerald-700" : listing.score >= 40 ? "text-amber-600" : "text-red-600"} />
                <Metric label={t("radar.detail.sumGap")}
                  value={gapPct !== null ? `${gapPct > 0 ? "+" : ""}${gapPct}%` : nc}
                  mention={gapPct !== null ? t("radar.detail.estimate") : undefined}
                  accent={gapPct !== null ? (gapPct <= 0 ? "text-emerald-700" : "text-red-600") : ""} />
                <Metric label={t("radar.detail.sumMargin")}
                  value={marginEst !== null ? `+${fmtEur(marginEst)}` : (dvf.available ? nc : t("radar.detail.toConfirm"))}
                  mention={marginEst !== null ? t("radar.detail.estimate") : undefined}
                  accent={marginEst !== null ? "text-emerald-700" : ""} />
                <Metric label={t("radar.detail.sumYield")} value={nc} mention={t("radar.detail.toConfirm")} />
              </div>
              <p className="mt-3 text-xs italic text-slate-400">{t("radar.detail.summaryNote")}</p>
            </Section>

            {/* Score */}
            <Section icon={Gauge} title={t("radar.detail.secScore")} testid="radar-sec-score">
              <p className="mb-2 text-sm text-slate-500">{t("radar.detail.scoreIntro")}</p>
              <List items={scoring.reasons} empty={t("radar.detail.notDetected")} />
            </Section>

            {/* IA */}
            <Section icon={Sparkles} title={t("radar.detail.secAI")} testid="radar-sec-ai">
              {ai.available ? (
                <div className="space-y-3 text-sm text-slate-600">
                  {ai.summary && <p className="text-slate-700">{ai.summary}</p>}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div><p className="mb-1 font-medium text-emerald-700">{t("radar.detail.strengths")}</p><List items={ai.strengths} empty={t("radar.detail.notDetected")} /></div>
                    <div><p className="mb-1 font-medium text-red-700">{t("radar.detail.weaknesses")}</p><List items={ai.weaknesses} empty={t("radar.detail.notDetected")} /></div>
                    <div><p className="mb-1 font-medium text-[#0F172A]">{t("radar.detail.signals")}</p><List items={ai.signals} empty={t("radar.detail.notDetected")} /></div>
                    <div><p className="mb-1 font-medium text-[#0F172A]">{t("radar.detail.keywords")}</p><List items={ai.detected_keywords} empty={t("radar.detail.notDetected")} /></div>
                  </div>
                  {ai.potentials && (
                    <div className="rounded-lg bg-slate-50 p-3">
                      {Object.entries(ai.potentials).filter(([, v]) => v).map(([k, v]) => (
                        <p key={k} className="text-sm"><span className="font-medium capitalize text-[#0F172A]">{k} :</span> {v}</p>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-400">{t("radar.detail.model")} {ai.model}</p>
                </div>
              ) : (
                <p className="text-sm text-amber-700">{t("radar.detail.aiUnavailable")} {ai.reason || t("radar.detail.aiInsufficient")}.{ai.reason && ai.reason.toLowerCase().includes("budget") ? t("radar.detail.aiBudget") : ""}</p>
              )}
              <p className="mt-2 text-xs text-slate-400">{t("radar.detail.rulesSignals")} {Object.keys(a.keywords_found || {}).length ? Object.entries(a.keywords_found).map(([k, v]) => `${k} (${v.join(", ")})`).join(" · ") : t("radar.detail.noneDetected")}</p>
            </Section>

            {/* Prix DVF */}
            <Section icon={TrendingDown} title={t("radar.detail.secDVF")} testid="radar-sec-dvf">
              {dvf.available ? (
                <div className="text-sm text-slate-600">
                  <div className="mb-2 flex flex-wrap gap-4">
                    <span>{t("radar.detail.askedPm2")} <b className="text-[#0F172A]">{pm2 ? `${pm2.toLocaleString("fr-FR")} €` : nc}</b></span>
                    <span>{t("radar.detail.median")} <b>{dvf.median_m2.toLocaleString("fr-FR")} €</b></span>
                    <span>{t("radar.detail.range")} {dvf.p25_m2.toLocaleString("fr-FR")}–{dvf.p75_m2.toLocaleString("fr-FR")} €</span>
                    <span className="text-slate-400">{t("radar.detail.salesRadius", { count: dvf.count, radius: dvf.radius_m })}</span>
                  </div>
                  {dvf.samples && (
                    <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-slate-100">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-500"><tr><th className="p-2 text-left">{t("radar.detail.colDate")}</th><th className="p-2 text-left">{t("radar.detail.colStreet")}</th><th className="p-2 text-right">{t("radar.detail.colSurface")}</th><th className="p-2 text-right">{t("radar.detail.colPrice")}</th><th className="p-2 text-right">{t("radar.detail.colPm2")}</th></tr></thead>
                        <tbody>{dvf.samples.map((s, i) => (
                          <tr key={i} className="border-t border-slate-50"><td className="p-2">{s.date}</td><td className="p-2">{s.voie || "—"}</td><td className="p-2 text-right">{s.surface} m²</td><td className="p-2 text-right">{s.prix.toLocaleString("fr-FR")} €</td><td className="p-2 text-right font-medium">{s.prix_m2.toLocaleString("fr-FR")}</td></tr>
                        ))}</tbody>
                      </table>
                    </div>
                  )}
                  <p className="mt-2 text-xs text-slate-400">{t("radar.detail.source", { source: dvf.source })}</p>
                </div>
              ) : <p className="text-sm text-slate-500">{dvf.reason || t("radar.detail.dvfUnavailable")} <span className="text-xs text-slate-400">({dvf.source})</span></p>}
            </Section>

            {/* PLU */}
            <Section icon={Landmark} title={t("radar.detail.secPLU")} testid="radar-sec-plu">
              <div className="grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                <p>{t("radar.detail.commune")} <b>{geo.city || listing.city || "—"}</b></p>
                <p>{t("radar.detail.parcel")} <b>{parcelle.available ? parcelle.reference : t("radar.detail.parcelUnidentified")}</b>{parcelle.contenance ? ` (${parcelle.contenance} m²)` : ""}</p>
                {!pluEntitled ? (
                  <p className="sm:col-span-2 flex items-center gap-2 rounded-lg bg-amber-50 p-3 text-amber-800" data-testid="radar-plu-locked">
                    <Lock className="h-4 w-4 shrink-0" />
                    Récupération & intégration du PLU/PLUi disponibles avec l'offre <b>Premium</b>. <a href="/abonnement" className="font-semibold text-[#C6A87C] underline">Passer au Premium</a>
                  </p>
                ) : (plu.available || plu.reglement_pdf_url || plu.document_download_url || plu.document_page_url) ? (<>
                  {plu.typezone ? (<>
                    <p>{t("radar.detail.zone")} <b>{plu.typezone} — {plu.libelle}</b></p>
                    <p className="sm:col-span-2 text-slate-500">{plu.libelong}</p>
                    <p className="sm:col-span-2 rounded-lg bg-amber-50 p-2 text-amber-800">
                      {plu.constructible_hint ? t("radar.detail.constructible") : ""}{t("radar.detail.pluConfirm")}
                    </p>
                  </>) : (
                    <p className="sm:col-span-2 rounded-lg bg-amber-50 p-2 text-amber-800" data-testid="radar-plu-zone-unconfirmed">{plu.reason || "Zonage précis non confirmé au point exact — consultez le règlement communal ci-dessous."}</p>
                  )}
                  <div className="sm:col-span-2 flex flex-wrap gap-2">
                    {plu.reglement_pdf_url && (
                      <a href={plu.reglement_pdf_url} target="_blank" rel="noreferrer" data-testid="radar-plu-reglement"
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#C6A87C] px-3 py-1.5 text-xs font-semibold text-[#0F172A] hover:bg-[#b8986a]">
                        <FileDown className="h-3.5 w-3.5" /> {t("radar.detail.pluReglement", "Règlement du PLU/PLUi (PDF)")}
                      </a>
                    )}
                    {plu.document_page_url && (
                      <a href={plu.document_page_url} target="_blank" rel="noreferrer" data-testid="radar-plu-pieces"
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                        <ExternalLink className="h-3.5 w-3.5" /> {t("radar.detail.pluPieces", "Toutes les pièces du document")}
                      </a>
                    )}
                    {plu.document_download_url && (
                      <a href={plu.document_download_url} target="_blank" rel="noreferrer" data-testid="radar-plu-download"
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                        <FileDown className="h-3.5 w-3.5" /> {t("radar.detail.pluArchive", "Archive complète (ZIP)")}
                      </a>
                    )}
                    {plu.document_viewer_url && (
                      <a href={plu.document_viewer_url} target="_blank" rel="noreferrer" data-testid="radar-plu-viewer"
                        className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                        <ExternalLink className="h-3.5 w-3.5" /> {t("radar.detail.pluViewer", "Voir sur le Géoportail de l'Urbanisme")}
                      </a>
                    )}
                  </div>
                </>) : plu.rnu ? (
                  <p className="sm:col-span-2 rounded-lg bg-slate-100 p-2 text-slate-600" data-testid="radar-plu-rnu">
                    {plu.reason}
                    {plu.document_viewer_url && <> — <a href={plu.document_viewer_url} target="_blank" rel="noreferrer" className="text-[#C6A87C] underline">{t("radar.detail.pluViewer", "Voir sur le Géoportail de l'Urbanisme")}</a></>}
                  </p>
                ) : (
                  <p className="sm:col-span-2 text-slate-500">
                    {plu.reason || t("radar.detail.pluUnavailable")}
                    {plu.document_viewer_url && <> — <a href={plu.document_viewer_url} target="_blank" rel="noreferrer" className="text-[#C6A87C] underline">{t("radar.detail.pluViewer", "Voir sur le Géoportail de l'Urbanisme")}</a></>}
                  </p>
                )}
                <p className="sm:col-span-2 text-xs text-slate-400">{t("radar.detail.source", { source: plu.source || "Géoportail de l'Urbanisme" })}{parcelle.available ? ` · ${parcelle.source}` : ""}</p>
              </div>
            </Section>

            {/* Risques */}
            <Section icon={ShieldAlert} title={t("radar.detail.secRisks")} testid="radar-sec-risks">
              {risks.available ? (
                risks.risques && risks.risques.length ? <List items={risks.risques} empty={t("radar.detail.notDetected")} /> : <p className="text-sm text-emerald-700">{t("radar.detail.noRisks")}</p>
              ) : <p className="text-sm text-slate-500">{t("radar.detail.risksUnavailable")}</p>}
              {risks.source && <p className="mt-2 text-xs text-slate-400">{t("radar.detail.source", { source: risks.source })}</p>}
            </Section>

            {/* Environnement — écoles, transports, gares, services (sources officielles gratuites, par géoloc réelle) */}
            <Section icon={MapPin} title={t("radar.detail.secEnv")} testid="radar-sec-env">
              {!envLoading && env?.available && env.location_score && (() => {
                const ls = env.location_score;
                const COL = { excellent: "text-emerald-700 bg-emerald-50 border-emerald-200", veryGood: "text-emerald-600 bg-emerald-50 border-emerald-200", good: "text-amber-600 bg-amber-50 border-amber-200", moderate: "text-slate-600 bg-slate-50 border-slate-200", insufficient: "text-slate-400 bg-slate-50 border-slate-200" };
                const factorLabel = { transports: t("radar.detail.locFTransports"), stations: t("radar.detail.locFStations"), schools: t("radar.detail.locFSchools"), services: t("radar.detail.locFServices") };
                return (
                  <div className={`mb-3 rounded-xl border p-3 ${COL[ls.label_key] || COL.insufficient}`} data-testid="radar-loc-score">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">{t("radar.detail.locTitle")}</p>
                        <p className="text-lg font-bold" data-testid="radar-loc-label">{t(`radar.detail.loc_${ls.label_key}`)}</p>
                      </div>
                      {ls.available && (
                        <button onClick={() => setShowLoc((v) => !v)} data-testid="radar-loc-toggle" className="rounded-full border border-current/30 px-3 py-1.5 text-xs font-semibold opacity-80 hover:opacity-100">
                          {showLoc ? t("radar.detail.locHide") : t("radar.detail.locShow")}
                        </button>
                      )}
                    </div>
                    {ls.available && showLoc && (
                      <div className="mt-3 grid grid-cols-2 gap-2" data-testid="radar-loc-factors">
                        {ls.factors.map((f) => (
                          <div key={f.key} className="rounded-lg bg-white/70 px-2.5 py-1.5">
                            <p className="text-[11px] text-slate-500">{factorLabel[f.key] || f.label}</p>
                            <p className="text-sm font-bold text-[#0F172A]">{f.value}</p>
                          </div>
                        ))}
                        <p className="col-span-2 mt-1 text-[11px] italic text-slate-500">{t("radar.detail.locNote")}</p>
                      </div>
                    )}
                  </div>
                );
              })()}
              {envLoading ? (
                <p className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> {t("radar.detail.envLoading")}</p>
              ) : !env || !env.available ? (
                <p className="text-sm italic text-slate-400">{nc}</p>
              ) : (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <EnvBlock icon={GraduationCap} title={t("radar.detail.envSchools")} data={env.schools} na={nc} testid="radar-env-schools" />
                  <EnvBlock icon={Train} title={t("radar.detail.envTransport")} data={env.transports} na={nc} testid="radar-env-transports" />
                  <EnvBlock icon={Landmark} title={t("radar.detail.envStations")} data={env.stations} na={nc} testid="radar-env-stations" />
                  <EnvBlock icon={ShoppingBag} title={t("radar.detail.envServices")} data={env.services} na={nc} testid="radar-env-services" />
                </div>
              )}
              {env?.services?.by_type && Object.keys(env.services.by_type).length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {Object.entries(env.services.by_type).slice(0, 8).map(([k, v]) => (
                    <span key={k} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{k} · {v}</span>
                  ))}
                </div>
              )}
              <p className="mt-3 text-xs italic text-slate-400">{t("radar.detail.envSourceNote")}</p>
            </Section>

            {/* Historique interne */}
            <Section icon={History} title={t("radar.detail.secHistory")} testid="radar-sec-history">
              <div className="grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                <p>{t("radar.detail.firstSeen")} <b>{(listing.first_seen || "").slice(0, 10) || "—"}</b></p>
                <p>{t("radar.detail.firstPrice")} <b>{listing.first_price ? `${Number(listing.first_price).toLocaleString("fr-FR")} €` : "—"}</b></p>
                <p>{t("radar.detail.currentPrice")} <b>{listing.current_price ? `${Number(listing.current_price).toLocaleString("fr-FR")} €` : "—"}</b></p>
                <p>{t("radar.detail.lastAnalyzed")} <b>{(listing.last_analyzed || "").slice(0, 10) || "—"}</b></p>
              </div>
              {listing.price_history && listing.price_history.length > 0 && (
                <div className="mt-3 space-y-1 text-sm">
                  {listing.price_history.map((h, i) => (
                    <p key={i} className="text-slate-600">{(h.date || "").slice(0, 10)} : {Number(h.previous).toLocaleString("fr-FR")} € → <b className="text-[#0F172A]">{Number(h.price).toLocaleString("fr-FR")} €</b></p>
                  ))}
                </div>
              )}
            </Section>

            {/* Carte */}
            {mapSrc && (
              <Section icon={MapPin} title={t("radar.detail.secMap")} testid="radar-sec-map">
                <p className="mb-2 text-sm text-slate-500">{geo.label}</p>
                <iframe title="carte" src={mapSrc} className="h-64 w-full rounded-lg border border-slate-200" loading="lazy" />
                <p className="mt-1 text-xs text-slate-400">{t("radar.detail.mapBase", { source: geo.source })}</p>
              </Section>
            )}

            {/* Sources */}
            <Section icon={FileText} title={t("radar.detail.secSources")} testid="radar-sec-sources">
              <List items={a.sources_used} empty={t("radar.detail.notDetected")} />
            </Section>
          </div>
        </div>
      </div>
      {showFoncier && <FoncierSheet listing={listing} onClose={() => setShowFoncier(false)} />}
    </>
  );
}
